import { MouseEvent, ReactNode, memo } from "react";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import {
  CheckCircle2Icon,
  CopyIcon,
  ExternalLinkIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Campaign, Creator } from "@/types/types";
import { EvePipelinePhase } from "@/types/eve";
import { evePayoutState, isEvePayoutSlotKey, evePhaseLabel, pipelineForCreator } from "@/lib/eve";
import { useCreatorsTableContext } from "../CreatorsTableContext";
import { fmtDate } from "./blocks";
import { resolvePhaseState } from "./phaseState";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";

/**
 * One table cell = one creator × one enabled pipeline phase, in the
 * TierColumnCell style: a mono uppercase state label over ONE key value.
 * The whole cell opens the PhaseDetailDialog (which carries the actions);
 * quick links (sign/claim) ride as inline icon buttons.
 */

interface PhaseColumnCellProps {
  creator: Creator;
  phase: EvePipelinePhase;
  /** The row's campaign — its tier pipeline gives the split-payout ordinal labels ("Payment 2"). */
  campaign?: Campaign | null;
}

function CellShell({
  label,
  labelClass,
  value,
  onClick,
  title,
  actions,
}: {
  label: ReactNode;
  labelClass?: string;
  value: ReactNode;
  onClick: () => void;
  title: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-0.5 min-w-0">
      <ScopeWrapper>
        <button
          onClick={onClick}
          title={title}
          className="flex flex-col items-start justify-center pl-1.5 pr-2.5 py-0.5 border-l border-dashed text-left min-w-0 max-w-48 hover:bg-muted rounded-r-md cursor-pointer"
        >
          <span className={cn("font-mono uppercase text-[11px] leading-4 truncate max-w-full flex items-center gap-1", labelClass ?? "text-muted-foreground")}>
            {label}
          </span>
          <span className="text-sm truncate max-w-full flex items-center gap-1">{value}</span>
        </button>
      </ScopeWrapper>
      {actions}
    </div>
  );
}

function LinkAction({ href, copy, tooltip }: { href?: string; copy?: string; tooltip: string }) {
  const stop = (e: MouseEvent) => e.stopPropagation();
  return (
    <span className="flex shrink-0" onClick={stop}>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          {href ? (
            <a href={href} target="_blank" rel="noreferrer">
              <Button size="icon" variant="muted-ghost" className="size-8 shrink-0 [&_svg]:size-3.75">
                <ExternalLinkIcon />
              </Button>
            </a>
          ) : (
            <Button
              size="icon"
              variant="muted-ghost"
              className="size-8 shrink-0 [&_svg]:size-3.75"
              onClick={() => {
                navigator.clipboard.writeText(copy!);
                toast.success("Link copied");
              }}
            >
              <CopyIcon />
            </Button>
          )}
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </span>
  );
}

/**
 * The one line worth reading once a phase is done — a key fact plus a quick
 * link action where one exists (signed doc, approved draft, live post).
 */
function completedSummary(
  creator: Creator,
  phase: EvePipelinePhase
): { value: ReactNode; actions?: ReactNode } {
  const evePhase = creator.eve_phase;
  const key = phase.key;

  if (key === "contract") {
    const doc = evePhase?.contract?.document_urls?.[0];
    return {
      value: "Signed",
      actions: doc ? <LinkAction href={doc} tooltip="Open signed contract" /> : undefined,
    };
  }
  if (isEvePayoutSlotKey(key)) {
    const p = evePayoutState(evePhase, key);
    const amount =
      p?.preview?.amount != null ? `${p.preview.amount} ${p.preview.currency ?? ""}`.trim() : null;
    return { value: amount ? `Paid ${amount}` : p?.claimed_at ? "Claimed" : "Paid" };
  }
  // gate/checks reset on the next phase entry, so wait/action keep it factual
  if (phase.shape === "wait") {
    const byTeam = evePhase?.gate?.released_by === "human";
    return { value: byTeam ? "Released by the team" : "Released" };
  }
  if (phase.shape === "action") {
    return { value: "Action ran" };
  }

  // Review-shaped (script/draft/posting/custom): surface the approved submission
  const artifacts = (evePhase?.artifacts ?? []).filter((a) => a.kind === key);
  const artifact =
    [...artifacts].reverse().find((a) => a.outcome === "approved") ?? artifacts[artifacts.length - 1];

  if (key === "posting") {
    const check = artifact?.post_checks?.find((c) => c.live === true) ?? artifact?.post_checks?.[0];
    const href =
      check?.canonical ?? check?.url ?? artifact?.links?.[0] ?? creator.uploaded_links?.[0]?.link;
    return {
      value: "Live post",
      actions: href ? <LinkAction href={href} tooltip="Open the live post" /> : undefined,
    };
  }
  const link = artifact?.links?.[0];
  if (link) {
    return {
      value: "Approved",
      actions: <LinkAction href={link} tooltip="Open the approved submission" />,
    };
  }
  if (artifact?.text_excerpt) {
    // Scripts are huge — clip hard so the cell can never stretch the table,
    // and fade the tail out instead of cutting it off mid-glyph
    const clipped = artifact.text_excerpt.replace(/\s+/g, " ").trim();
    if (clipped.length <= 28) return { value: clipped };
    return {
      value: (
        <span className="max-w-full overflow-hidden whitespace-nowrap mask-r-from-70%">
          {clipped.slice(0, 80)}
        </span>
      ),
    };
  }
  return { value: "Completed" };
}

function PhaseColumnCell({ creator, phase, campaign }: PhaseColumnCellProps) {
  const { setPhaseDialog } = useCreatorsTableContext();
  const evePhase = creator.eve_phase;
  const key = phase.key;
  const label = evePhaseLabel(phase, pipelineForCreator(campaign, creator));
  const openDialog = () => setPhaseDialog({ creatorId: creator._id, phaseKey: key });
  // Same resolver the column filter's checklist reads — see phaseState.ts.
  const state = resolvePhaseState(creator, phase);

  if (state.kind === "done") {
    const completedAt = fmtDate(state.completedEntry?.completed_at ?? evePhase?.contract?.completed_at);
    const done = completedSummary(creator, phase);
    return (
      <CellShell
        label={
          <>
            <CheckCircle2Icon className="size-3 text-success shrink-0" />
            {completedAt ?? "Done"}
          </>
        }
        value={done.value}
        onClick={openDialog}
        title={`${label} completed — view details`}
        actions={done.actions}
      />
    );
  }

  if (state.kind === "not_started") {
    return <span className="text-muted-foreground italic pl-2 text-xs">missing</span>;
  }

  // ── Current phase: STATE label + one key value ────────────────────────
  const nudges = evePhase?.nudge_count ?? 0;
  const revisions = evePhase?.revision_count ?? 0;
  const nudgeSuffix = nudges > 0 ? ` · ${nudges} nudge${nudges === 1 ? "" : "s"}` : "";
  const sentOrEntered = fmtDate(evePhase?.sent_at) ?? fmtDate(evePhase?.entered_at);
  const title = `${label} — view details`;

  // Contract
  if (key === "contract") {
    if (state.kind === "declined") {
      return (
        <CellShell
          label="Declined"
          labelClass="text-destructive"
          value={fmtDate(evePhase?.contract?.declined_at) ?? "By creator"}
          onClick={openDialog}
          title={title}
        />
      );
    }
    if (state.kind === "sending") {
      return (
        <CellShell
          label="Sending"
          value={
            <>
              <Spinner className="size-3 shrink-0 text-muted-foreground" /> Contract email
            </>
          }
          onClick={openDialog}
          title={title}
        />
      );
    }
    const signUrl = evePhase?.contract?.sign_url;
    return (
      <CellShell
        label="Awaiting signature"
        value={`${sentOrEntered ? `Sent ${sentOrEntered}` : "Link ready"}${nudgeSuffix}`}
        onClick={openDialog}
        title={title}
        actions={
          signUrl ? (
            <>
              <LinkAction copy={signUrl} tooltip="Copy signing link" />
              <LinkAction href={signUrl} tooltip="Open signing page" />
            </>
          ) : undefined
        }
      />
    );
  }

  // Payout slots (payment_1 / payment_2 / the final payment) — one engine,
  // the cell reads the slot's own subdoc.
  if (isEvePayoutSlotKey(key)) {
    const slotState = state.payoutState;
    if (state.kind === "awaiting_claim") {
      return (
        <CellShell
          label="Awaiting claim"
          value={`${slotState?.preview?.amount != null ? `${slotState.preview.amount} ${slotState.preview.currency}` : "Payout sent"}${slotState?.payout_status ? ` · ${slotState.payout_status}` : ""}`}
          onClick={openDialog}
          title={title}
          actions={slotState?.payout_link ? <LinkAction href={slotState.payout_link} tooltip="Open claim link" /> : undefined}
        />
      );
    }
    if (state.kind === "confirm_payout") {
      const p = slotState!.preview!;
      return (
        <CellShell
          label="Confirm payout"
          labelClass="text-success"
          value={`${p.amount} ${p.currency}${p.net != null ? ` · net ${p.net}` : ""}`}
          onClick={openDialog}
          title={`${label} — review and confirm`}
        />
      );
    }
    return (
      <CellShell
        label="Preparing"
        value={`KYC: ${slotState?.kyc_status ?? "checking"}`}
        onClick={openDialog}
        title={title}
      />
    );
  }

  // Custom action step
  if (state.kind === "running") {
    const attempts = evePhase?.action_attempts ?? 0;
    const checks = (evePhase?.checks ?? []).filter((c) => c.phase_key === key).length;
    return (
      <CellShell
        label="Running"
        value={`${attempts > 0 ? `Attempt ${attempts}` : "Amy's side"}${checks > 0 ? ` · ${checks} check${checks === 1 ? "" : "s"}` : ""}`}
        onClick={openDialog}
        title={title}
      />
    );
  }

  // Custom wait hold
  if (state.kind === "stalled" || state.kind === "holding") {
    const stalled = state.kind === "stalled";
    return (
      <CellShell
        label={stalled ? "Stalled" : "Holding"}
        labelClass={stalled ? "text-warning" : undefined}
        value={`Since ${fmtDate(evePhase?.entered_at) ?? "recently"}`}
        onClick={openDialog}
        title={title}
      />
    );
  }

  // Review-shaped phases (script/draft/posting/custom review)
  if (state.kind === "needs_review") {
    const latest = [...(evePhase?.artifacts ?? [])].reverse().find((a) => a.kind === key);
    const rec = latest?.recommendation;
    return (
      <CellShell
        label="Needs review"
        labelClass="text-indigo-500"
        value={
          rec
            ? rec.verdict === "approve"
              ? "Amy recommends approve"
              : "Amy: needs your eyes"
            : `Submitted ${fmtDate(latest?.at) ?? "just now"}`
        }
        onClick={openDialog}
        title={`${label} — review the submission`}
      />
    );
  }
  return (
    <CellShell
      label={revisions > 0 ? `Revision #${revisions}` : "Waiting on creator"}
      value={`${sentOrEntered ? `Sent ${sentOrEntered}` : "No email yet"}${nudgeSuffix}`}
      onClick={openDialog}
      title={title}
    />
  );
}

export default memo(PhaseColumnCell);
